import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Tooltip, Typography } from "@mui/material";
import { ErrorModal } from "../../components/ModalAlert";
import { LoaderSpinner } from "../../components/spinner/loader";
import DataTable from "../../components/ui/DataTable";
import { formatNumber, formatCurrencyManual } from "../../utils/formatters";
import { Num } from "../../components/ui";
import { tokens } from "../../theme";
import { ticketObjectStatus } from "../../constants/ticketStatus";
import supabase from "../../supabase";
import HeadClientAccount from "./headClientAccount";
import moment from "moment";
import { ticketStatus } from "../../constants/ticketStatus";
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import Swal from 'sweetalert2';
import { resendBill } from "../../apiRequests/post";

/**
 * Pie de una tabla con su total. Antes cada uno era una franja con
 * `backgroundColor: '#f5f5f5'` fijo, que en modo oscuro quedaba como una banda
 * blanca cruzando la pantalla.
 */
const TotalRow = ({ label, value, destacado }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "flex-end",
      gap: "10px",
      flexWrap: "wrap",
      padding: "12px 16px",
      borderRadius: `${tokens.radiusCard}px`,
      border: `1px solid ${destacado ? tokens.brandBorder : tokens.border}`,
      backgroundColor: destacado ? tokens.brandTint : tokens.surface,
    }}
  >
    <Typography
      sx={{ fontSize: "12.5px", fontWeight: 500, color: destacado ? tokens.brandDarker : tokens.ink3 }}
    >
      {label}
    </Typography>
    <Num
      sx={{ fontSize: "15px", fontWeight: 600, color: destacado ? tokens.brandDarker : tokens.ink }}
    >
      {formatCurrencyManual(value)}
    </Num>
  </Box>
);

const ClientCurrentAccount = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState(location?.state?.client || null);
  const [tickets, setTickets] = useState([]);
  const [payments, setPayments] = useState([]);
  const [totalFacturas, setTotalFacturas] = useState(0);
  const [totalFacturasPendientes, setTotalFacturasPendientes] = useState(0);
  const [totalPagos, setTotalPagos] = useState(0);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [filterPaymentId, setFilterPaymentId] = useState(location?.state?.filterPaymentId || null);

  const formatNumber = (number) => {
    return number.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true
    });
  };

  useEffect(() => {
    if (location?.state?.filterPaymentId) {
      setFilterPaymentId(location.state.filterPaymentId);
    }
  }, [location?.state?.filterPaymentId]);

  useEffect(() => {
    if (client) {
      getClientData();
    }
  }, [client]);

  useEffect(() => {
    if (filterPaymentId && payments.length > 0) {
      const filtered = payments.filter(payment => payment.paymentId === filterPaymentId);
      setFilteredPayments(filtered);
    } else {
      setFilteredPayments(payments);
    }
  }, [payments, filterPaymentId]);

  useEffect(() => {
    // Calcular total de TODAS las facturas emitidas (suma con cotización cuando USD)
    const totalFacturasEmitidas = tickets.reduce((sum, ticket) => {
      const monto = ticket.montoNumerico ?? (parseFloat(String(ticket.monto).replace('$', '').replace(/\./g, '').replace(',', '.').trim()) || 0);
      return sum + (Number.isFinite(monto) ? monto : 0);
    }, 0);
    setTotalFacturas(Number(totalFacturasEmitidas.toFixed(2)));

    // Calcular total de facturas AUTORIZADAS (pendientes) para el cálculo del saldo (suma con cotización cuando USD)
    const totalFacturasPendientes = tickets
      .filter(ticket => ticket.status === ticketObjectStatus.autorizada)
      .reduce((sum, ticket) => {
        const monto = ticket.montoNumerico ?? (parseFloat(String(ticket.monto).replace('$', '').replace(/\./g, '').replace(',', '.').trim()) || 0);
        return sum + (Number.isFinite(monto) ? monto : 0);
      }, 0);
    setTotalFacturasPendientes(Number(totalFacturasPendientes.toFixed(2)));
  }, [tickets]);

  useEffect(() => {
    // Calcular total de pagos (suma con cotización cuando USD; montoNumerico ya convertido)
    const totalPagos = payments.reduce((sum, payment) => {
      const monto = payment.montoNumerico ?? 0;
      return sum + (Number.isFinite(monto) ? monto : 0);
    }, 0);
    setTotalPagos(Number(totalPagos.toFixed(2)));
  }, [payments]);

  const getClientData = async () => {
    setLoading(true);
    try {
      // Obtener facturas pendientes o autorizadas
      const { data: ticketsData, error: ticketsError } = await supabase
        .from("tickets")
        .select(`
          *,
          ticketDetails(
            price,
            quantity,
            ivaPercentage
          )
        `)
        .eq("clientId", client.clientId)
        .in("status", [ticketObjectStatus.autorizada, ticketObjectStatus.pagada]);

      if (ticketsError) throw ticketsError;

      // Cotización y hasUSD por ticket (para montos en USD)
      const ticketIds = (ticketsData || []).map(t => t.ticketId).filter(Boolean);
      const ticketToCotiz = new Map();
      if (ticketIds.length > 0) {
        const { data: ordersData, error: ordersErr } = await supabase
          .from('orders')
          .select('ticketId, currencyRate, orderDetails(currency)')
          .in('ticketId', ticketIds);
        if (!ordersErr && ordersData && ordersData.length > 0) {
          for (const o of ordersData) {
            const rate = parseFloat(o.currencyRate);
            const cotizacion = !isNaN(rate) && rate > 0 ? rate : 1;
            const details = o.orderDetails || [];
            const hasUSD = details.some(d => d && (d.currency === 2 || d.currency === '2'));
            ticketToCotiz.set(o.ticketId, { cotizacion, hasUSD });
          }
        }
      }

      // Procesamos los tickets: el monto es el total de la factura en su moneda (pesos o USD).
      // NO aplicamos cotización: el ticket guarda precios ya en la moneda facturada (PES o DOL).
      const processedTickets = ticketsData
        .map(ticket => {
          const totalAmount = ticket.ticketDetails.reduce((sum, detail) => {
            const price = Number(detail.price) || 0;
            const quantity = Number(detail.quantity) || 0;
            const ivaPercentage = Number(detail.ivaPercentage) || 0;
            return sum + (price * quantity * (1 + (ivaPercentage / 100)));
          }, 0);
          const displayAmountRounded = Number(totalAmount.toFixed(2));

          return {
            ...ticket,
            fecha: moment(ticket.createdAt).format("DD/MM/YY"),
            comprobante: `${ticket.ticketType || ''} ${ticket.ticketNumber || ''}`,
            description: ticket.description || '',
            status: ticket.status,
            monto: formatCurrencyManual(displayAmountRounded),
            montoNumerico: displayAmountRounded,
            createdAtTimestamp: new Date(ticket.createdAt).getTime()
          };
        })
        .sort((a, b) => {
          // Primero ordenar por estado (autorizadas primero)
          if (a.status === ticketObjectStatus.autorizada && b.status !== ticketObjectStatus.autorizada) return -1;
          if (a.status !== ticketObjectStatus.autorizada && b.status === ticketObjectStatus.autorizada) return 1;
          
          // Si tienen el mismo estado, ordenar por fecha (más reciente primero)
          return b.createdAtTimestamp - a.createdAtTimestamp;
        });

      setTickets(processedTickets);

      const { data: paymentsTicketData, error: paymentsError } = await supabase
        .from("paymentsTicket")
        .select(`
          paymentTicketId,
          payments (
            paymentId,
            totalAmount,
            description,
            createdAt,
            paymentDetails(
              paymentMethod,
              amount
            )
          ),
          tickets!inner (
            ticketId,
            clientId,
            ticketType,
            ticketNumber,
            createdAt
          )
        `)
        .eq("tickets.clientId", client.clientId);

      if (paymentsError) throw paymentsError;

      // Primero, necesitamos agrupar todos los métodos de pago por paymentId
      const paymentMethodsByPaymentId = paymentsTicketData.reduce((acc, paymentTicket) => {
        const paymentId = paymentTicket.payments.paymentId;
        
        // Verificamos que payments y paymentDetails existan
        if (paymentTicket.payments && paymentTicket.payments.paymentDetails && 
            paymentTicket.payments.paymentDetails.length > 0) {
          
          // Iteramos sobre todos los métodos de pago
          paymentTicket.payments.paymentDetails.forEach(detail => {
            const paymentMethod = detail.paymentMethod;
            const methodName = paymentMethod === 0 ? 'Saldo a Favor' : 
                              paymentMethod === 1 ? 'Efectivo' : 
                              paymentMethod === 2 ? 'Transferencia' : 
                              paymentMethod === 3 ? 'Cheque' : 
                              paymentMethod === 4 ? 'Tarjeta de Crédito' : 
                              paymentMethod === 5 ? 'Tarjeta de Débito' : 'Otro';
            
            if (!acc[paymentId]) {
              acc[paymentId] = {
                methods: [methodName],
                paymentMethod: paymentMethod
              };
            } else if (!acc[paymentId].methods.includes(methodName)) {
              acc[paymentId].methods.push(methodName);
            }
          });
        }
        
        return acc;
      }, {});

      // Luego, en la reducción principal, usamos esta información
      const groupedPayments = paymentsTicketData.reduce((acc, paymentTicket) => {
        const paymentId = paymentTicket.payments.paymentId;
        
        // Determinamos el método de pago principal (para la lógica de saldo a favor)
        let primaryPaymentMethod = 1; // Por defecto
        if (paymentTicket.payments && paymentTicket.payments.paymentDetails && 
            paymentTicket.payments.paymentDetails.length > 0) {
          primaryPaymentMethod = paymentTicket.payments.paymentDetails[0].paymentMethod;
        }
        
        // Calculamos el monto total EXCLUYENDO "Saldo a Favor" (paymentMethod = 0)
        let totalAmount = 0;
        
        // Siempre calculamos sumando solo los amount de paymentDetails que NO sean "Saldo a Favor"
        if (paymentTicket.payments && paymentTicket.payments.paymentDetails) {
          paymentTicket.payments.paymentDetails.forEach(detail => {
            if (detail.paymentMethod !== 0) {
              totalAmount += Number(detail.amount || 0);
            }
          });
        }
        
        // Si no hay paymentDetails o todos son "Saldo a Favor", el monto será 0
        // No usamos totalAmount de la tabla payments porque puede incluir saldos a favor
        
        if (!acc[paymentId]) {
          // Si es un nuevo pago, lo creamos con su primer ticket
          acc[paymentId] = {
            paymentId: paymentTicket.payments.paymentId,
            fecha: moment(paymentTicket.payments.createdAt).format("DD/MM/YY"),
            comprobante: `Recibo Nro ${paymentTicket.payments.paymentId}`,
            description: paymentTicket.payments.description || '',
            monto: `$ ${totalAmount.toLocaleString('es-AR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
              useGrouping: true
            })}`,
            montoNumerico: totalAmount, // Guardamos el valor numérico para cálculos posteriores
            formaPago: paymentMethodsByPaymentId[paymentId] ? paymentMethodsByPaymentId[paymentId].methods.join(', ') : 'No especificado',
            tickets: [{
              ticketId: paymentTicket.tickets.ticketId,
              type: paymentTicket.tickets.ticketType,
              number: paymentTicket.tickets.ticketNumber,
              createdAt: paymentTicket.tickets.createdAt
            }],
            createdAtTimestamp: new Date(paymentTicket.payments.createdAt).getTime(),
            paymentMethod: primaryPaymentMethod
          };
        } else {
          // Si ya existe, solo agregamos el ticket a la lista
          if (!acc[paymentId].tickets.some(t => 
            t.type === paymentTicket.tickets.ticketType && 
            t.number === paymentTicket.tickets.ticketNumber)) {
            acc[paymentId].tickets.push({
              ticketId: paymentTicket.tickets.ticketId,
              type: paymentTicket.tickets.ticketType,
              number: paymentTicket.tickets.ticketNumber,
              createdAt: paymentTicket.tickets.createdAt
            });
          }
        }
        
        return acc;
      }, {});

      // Reutilizamos ticketToCotiz (de facturas) para pagos; los tickets de pagos son facturas del cliente

      // Convertimos el objeto a array, aplicamos valor * cotización cuando USD, y formateamos
      const uniquePayments = Object.values(groupedPayments)
        .map(payment => {
          const firstTicketId = payment.tickets[0]?.ticketId;
          const info = firstTicketId ? ticketToCotiz.get(firstTicketId) : null;
          const cotizacion = info?.cotizacion ?? 1;
          const hasUSD = info?.hasUSD ?? false;
          const mult = hasUSD ? cotizacion : 1;
          const rawAmount = payment.montoNumerico ?? 0;
          const displayAmount = rawAmount * mult;
          const displayAmountRounded = Number(displayAmount.toFixed(2));
          return {
            ...payment,
            monto: formatCurrencyManual(displayAmountRounded),
            montoNumerico: displayAmountRounded,
            detalle: payment.tickets
              .map(t => `${t.type} ${t.number}`)
              .join(', ')
          };
        })
        .sort((a, b) => b.createdAtTimestamp - a.createdAtTimestamp);

      setPayments(uniquePayments);

    } catch (error) {
      ErrorModal({ title: "Error obteniendo los datos", text: error.message });
      console.error("Error completo:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendBill = async (ticket) => {
    const result = await Swal.fire({
      title: 'Reenviar factura?',
      text: `Se reenviará ${ticket.ticketType} ${ticket.ticketNumber} al Cliente.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#b91411',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, reenviar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const response = await resendBill(ticket.ticketType, ticket.ticketNumber);
        
        if (response.error === "S") {
          throw new Error(response.errores || "Error al reenviar la factura");
        }
        
        Swal.fire({
          title: '¡Factura reenviada!',
          text: 'La factura ha sido reenviada exitosamente al cliente.',
          icon: 'success',
          confirmButtonColor: '#b91411'
        });
      } catch (error) {
        Swal.fire({
          title: 'Error',
          text: error.message || 'No se pudo reenviar la factura. Intente nuevamente.',
          icon: 'error',
          confirmButtonColor: '#b91411'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const ticketsColumns = [
    {
      name: "fecha",
      label: "Fecha",
      options: { filter: true, sort: true }
    },
    {
      name: "comprobante",
      label: "N° Factura",
      options: { 
        filter: true, 
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const ticket = tickets[tableMeta.rowIndex];
          if (ticket) {
            return (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();            
                  const ticketData = {
                    ticketId: ticket.ticketId,
                    ticketNumber: ticket.ticketNumber,
                    ticketType: ticket.ticketType,
                    createdAt: ticket.createdAt
                  };
                                    
                  navigate("/ticket-details-form", {
                    state: { 
                      ticket: ticketData
                    }
                  });
                }}
                style={{ color: 'var(--sup-brand-dark)', textDecoration: 'none', fontWeight: 500 }}
              >
                {value}
              </a>
            );
          }
          return value;
        }
      }
    },
    {
      name: "description",
      label: "Descripción",
      options: { filter: true, sort: true }
    },
    {
      name: "status",
      label: "Estado",
      options: { 
        filter: true, 
        sort: true,
        customBodyRender: (value) => {
          const status = ticketStatus.find(s => s.value === value);
          return status ? status.label : 'Desconocido';
        }
      }
    },
    {
      name: "monto",
      label: "Monto",
      options: { filter: true, sort: true }
    },
    {
      name: "actions",
      label: "Acciones",
      options: { 
        filter: false, 
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const ticket = tickets[tableMeta.rowIndex];
          const isPayEnabled = ticket.status === ticketObjectStatus.autorizada;
          
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Pagar Factura">
                <AttachMoneyIcon
                  style={{
                    cursor: isPayEnabled ? "pointer" : "default",
                    color: isPayEnabled ? "#b91411" : "#ccc",
                    margin: "0 5px"
                  }}
                  onClick={() => {
                    if (isPayEnabled) {
                      navigate("/register-bill-payment", {
                        state: { client: client, ticket: ticket },
                      });
                    }
                  }}
                />
              </Tooltip>
              <Tooltip title="Reenviar Factura">
                <MailOutlineIcon
                  style={{
                    cursor: "pointer",
                    color: "#b91411",
                    margin: "0 5px"
                  }}
                  onClick={() => handleResendBill(ticket)}
                />
              </Tooltip>
            </div>
          );
        }
      }
    }
  ];

  const paymentsColumns = [
    {
      name: "fecha",
      label: "Fecha",
      options: { filter: true, sort: true }
    },
    {
      name: "comprobante",
      label: "N° Recibo",
      options: { 
        filter: true, 
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const payment = filteredPayments[tableMeta.rowIndex];
          if (payment) {
            // Extraer el número de recibo del texto (ej: "Recibo Nro 163")
            const reciboNumero = value.split('Nro ')[1];
            return (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/cash-movements", {
                    state: { 
                      searchRecibo: `Recibo Nro ${reciboNumero}`,
                      fechaDesde: "dd/mm/aaaa"
                    }
                  });
                }}
                style={{ color: 'var(--sup-brand-dark)', textDecoration: 'none', fontWeight: 500 }}
              >
                {value}
              </a>
            );
          }
          return value;
        }
      }
    },
    {
      name: "description",
      label: "Descripción",
      options: { filter: true, sort: true }
    },
    {
      name: "formaPago",
      label: "Forma de Pago",
      options: { filter: true, sort: true }
    },
    {
      name: "detalle",
      label: "Nº Factura",
      options: { 
        filter: true, 
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const payment = payments[tableMeta.rowIndex];
          if (payment && payment.tickets) {
            return (
              <div>
                {payment.tickets.map((ticket, index) => (
                  <React.Fragment key={index}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/ticket-details-form", {
                          state: { 
                            ticket: {
                              ticketId: ticket.ticketId,
                              ticketNumber: ticket.number,
                              ticketType: ticket.type,
                              createdAt: ticket.createdAt
                            }
                          }
                        });
                      }}
                      style={{ color: 'var(--sup-brand-dark)', textDecoration: 'none', fontWeight: 500 }}
                    >
                      {`${ticket.type} ${ticket.number}`}
                    </a>
                    {index < payment.tickets.length - 1 ? ', ' : ''}
                  </React.Fragment>
                ))}
              </div>
            );
          }
          return value;
        }
      }
    },
    {
      name: "monto",
      label: "Monto",
      options: { 
        filter: true, 
        sort: true,
        customBodyRender: (value, tableMeta) => {
          return value;
        }
      }
    }
  ];

  const options = {
    responsive: "simple",
    selectableRows: "none",
    rowsPerPage: 25,
  };

  return loading ? (
    <div style={{ width: "100%", height: "200px", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <LoaderSpinner />
    </div>
  ) : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%", minWidth: 0 }}>
      <HeadClientAccount 
        client={client} 
        totalFacturas={totalFacturasPendientes} 
        totalPagos={totalPagos} 
      />
      
      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <DataTable
          data={tickets}
          columns={ticketsColumns}
          heading="Facturas emitidas"
          emptyMessage="Este cliente no tiene facturas emitidas"
          options={{
            ...options,
            downloadOptions: { filename: "facturas-emitidas.csv", separator: ";" },
          }}
        />
        <TotalRow label="Total facturas emitidas" value={totalFacturas} />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <DataTable
          data={filteredPayments}
          columns={paymentsColumns}
          heading="Pagos recibidos"
          emptyMessage="Este cliente no registra pagos"
          options={{
            ...options,
            downloadOptions: { filename: "pagos-recibidos.csv", separator: ";" },
          }}
        />
        <TotalRow label="Total pagos recibidos" value={totalPagos} />
        <TotalRow label="Saldo a favor del cliente" value={Number(client.balance || 0)} destacado />
      </Box>
    </Box>
  );
};

export default ClientCurrentAccount; 